/**
 * Soft Vuex migration telemetry → /log/debug.
 *
 * 1) Illegal mutations (prod only, sampled): Vuex 4 strips the strict assert in
 *    production. We install a deep watch for a small cohort only (costly).
 * 2) Unknown getters / actions / mutations (any env when error logging is on):
 *    Vuex only console.errors these in non-prod. We Proxy getters and wrap
 *    commit/dispatch so missing types are reported in prod too.
 *    Cheap — no deep watch; one POST per key/type per store/session.
 *
 * Still not captured at runtime: third-party console noise, static patterns that
 * never warn, compile/build-only issues.
 */
import { watch } from 'vue';
import logger from '@/utils/global-logger';
import { DebugCode } from '@/utils/error-codes/debug';

const DEFAULT_SAMPLE_RATE = 0.05;

const reportedKeys = new Set();
const reportedUnknownGetters = new Set();
const reportedUnknownMutations = new Set();
const reportedUnknownActions = new Set();

/** Session-level decision, computed once so all stores agree. */
let cohortDecision;

function isProd() {
  return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
}

function isCbDebugEnabled() {
  return typeof window !== 'undefined' && !!window.__cb_debug;
}

function isErrorLoggingEnabled() {
  return typeof window !== 'undefined' && !!(window.initInfo && window.initInfo.error_logging_enabled === true);
}

function getBuildTag() {
  return (typeof process !== 'undefined' && process.env && process.env.CB_BUILD_TAG) || '0.0.0';
}

function getLocationMeta() {
  if (typeof window === 'undefined' || !window.location) {
    return { path: '', href: '' };
  }
  const path = window.location.pathname || '';
  // Strip query/hash — HP claim/session tokens live in the query string.
  const origin = window.location.origin || '';
  const href = origin
    ? `${origin}${path}`
    : (window.location.href || '').split(/[?#]/)[0];
  return { path, href };
}

function getSampleRate() {
  const raw = typeof window !== 'undefined' && window.initInfo && window.initInfo.vuex_soft_strict_sample_rate;
  const rate = Number(raw);
  if (Number.isFinite(rate) && rate >= 0 && rate <= 1) {
    return rate;
  }
  return DEFAULT_SAMPLE_RATE;
}

function isReportableGetterKey(prop) {
  if (typeof prop !== 'string' || prop.length === 0 || prop === 'then') {
    return false;
  }
  // Vue 3 reactivity probes proxies with __v_isRef / __v_raw / etc. — not real getters.
  if (prop.startsWith('__v_') || prop.startsWith('__')) {
    return false;
  }
  return true;
}

/** Resolve Vuex type from string or object-style `{ type }` payload (same as unifyObjectStyle). */
function resolveVuexType(typeOrObject) {
  if (typeOrObject && typeof typeOrObject === 'object' && typeof typeOrObject.type === 'string') {
    return typeOrObject.type;
  }
  return typeof typeOrObject === 'string' ? typeOrObject : undefined;
}

/**
 * Native Vuex strict mode: on only in dev so the built-in assert still fires
 * during development/QA. In prod we use the sampled soft reporter instead.
 * @returns {boolean}
 */
export function getVuexStrictMode() {
  return !isProd();
}

/**
 * Decide (once per session) whether this session reports illegal mutations.
 * Requires error logging to be enabled. Enabled when:
 * - cb_debug is on, or
 * - initInfo.vuex_soft_strict_enabled === true (explicit cohort), or
 * - a per-session sample hits initInfo.vuex_soft_strict_sample_rate (default 5%).
 * @param {() => number} [randomFn]
 * @returns {boolean}
 */
export function isVuexSoftReportEnabled(randomFn = Math.random) {
  if (cohortDecision !== undefined) {
    return cohortDecision;
  }
  if (!isProd()) {
    cohortDecision = false;
    return cohortDecision;
  }
  if (!isErrorLoggingEnabled()) {
    cohortDecision = false;
    return cohortDecision;
  }
  if (isCbDebugEnabled()) {
    cohortDecision = true;
    return cohortDecision;
  }
  const explicit = typeof window !== 'undefined' && window.initInfo && window.initInfo.vuex_soft_strict_enabled === true;
  cohortDecision = explicit || randomFn() < getSampleRate();
  return cohortDecision;
}

/** @private — reset session/dedupe state for unit tests. */
export function resetVuexSoftIllegalMutationReport() {
  reportedKeys.clear();
  reportedUnknownGetters.clear();
  reportedUnknownMutations.clear();
  reportedUnknownActions.clear();
  cohortDecision = undefined;
}

/**
 * Install the soft reporter on a store. No-op unless the session is in the
 * reporting cohort, so the deep watch cost is paid only by that cohort.
 * @param {import('vuex').Store<any>} store
 * @param {{ storeName?: string }} [options]
 * @returns {(() => void) | undefined} stop handle (when active)
 */
export function enableVuexSoftIllegalMutationReport(store, options = {}) {
  if (!store || !store._state || typeof store._committing !== 'boolean') {
    // Vuex internals moved (upgrade) — degrade gracefully rather than throw.
    return undefined;
  }
  if (!isVuexSoftReportEnabled()) {
    return undefined;
  }

  const storeName = options.storeName || 'store';

  return watch(
    () => store._state.data,
    () => {
      if (store._committing) {
        return;
      }

      const { path, href } = getLocationMeta();
      const dedupeKey = `${storeName}|${path}`;
      if (reportedKeys.has(dedupeKey)) {
        return;
      }
      reportedKeys.add(dedupeKey);

      // /log/debug: migration telemetry, not a real failure — kept out of Sentry / error counts.
      logger.debug(DebugCode.VUEX_ILLEGAL_MUTATION, {
        path,
        href,
        build: getBuildTag(),
        store: storeName,
        message: 'do not mutate vuex store state outside mutation handlers',
      });
    },
    { deep: true, flush: 'sync' }
  );
}

function reportUnknownGetter(storeName, getterName) {
  if (!isErrorLoggingEnabled()) {
    return;
  }
  const dedupeKey = `${storeName}|${getterName}`;
  if (reportedUnknownGetters.has(dedupeKey)) {
    return;
  }
  reportedUnknownGetters.add(dedupeKey);

  const { path, href } = getLocationMeta();
  logger.debug(DebugCode.VUEX_UNKNOWN_GETTER, {
    path,
    href,
    build: getBuildTag(),
    store: storeName,
    getter: getterName,
    message: `unknown getter: ${getterName}`,
  });
}

function reportUnknownMutation(storeName, mutationType) {
  if (!isErrorLoggingEnabled()) {
    return;
  }
  const dedupeKey = `${storeName}|${mutationType}`;
  if (reportedUnknownMutations.has(dedupeKey)) {
    return;
  }
  reportedUnknownMutations.add(dedupeKey);

  const { path, href } = getLocationMeta();
  logger.debug(DebugCode.VUEX_UNKNOWN_MUTATION, {
    path,
    href,
    build: getBuildTag(),
    store: storeName,
    mutation: mutationType,
    message: `unknown mutation type: ${mutationType}`,
  });
}

function reportUnknownAction(storeName, actionType) {
  if (!isErrorLoggingEnabled()) {
    return;
  }
  const dedupeKey = `${storeName}|${actionType}`;
  if (reportedUnknownActions.has(dedupeKey)) {
    return;
  }
  reportedUnknownActions.add(dedupeKey);

  const { path, href } = getLocationMeta();
  logger.debug(DebugCode.VUEX_UNKNOWN_ACTION, {
    path,
    href,
    build: getBuildTag(),
    store: storeName,
    action: actionType,
    message: `unknown action type: ${actionType}`,
  });
}

/**
 * Proxy store.getters so missing keys are reported to /log/debug.
 * Catches mapGetters `in` checks (dev) and direct property access (prod + dev).
 * Cheap — no deep watch. No-op if getters already proxied or store is invalid.
 * Note: Vuex registerModule/hotUpdate replaces store.getters; re-call after that if needed.
 * @param {import('vuex').Store<any>} store
 * @param {{ storeName?: string }} [options]
 * @returns {boolean} true when proxy installed
 */
export function enableVuexUnknownGetterReport(store, options = {}) {
  if (!store || !store.getters || typeof store.getters !== 'object') {
    return false;
  }
  if (store.getters.__vuexUnknownGetterProxy) {
    return false;
  }

  const storeName = options.storeName || 'store';
  const target = store.getters;

  store.getters = new Proxy(target, {
    has(obj, prop) {
      const exists = Reflect.has(obj, prop);
      if (!exists && isReportableGetterKey(prop)) {
        reportUnknownGetter(storeName, prop);
      }
      return exists;
    },
    get(obj, prop, receiver) {
      if (prop === '__vuexUnknownGetterProxy') {
        return true;
      }
      if (typeof prop === 'symbol') {
        return Reflect.get(obj, prop, receiver);
      }
      const exists = Reflect.has(obj, prop);
      if (!exists && isReportableGetterKey(prop)) {
        reportUnknownGetter(storeName, prop);
        return undefined;
      }
      return Reflect.get(obj, prop, receiver);
    },
  });

  return true;
}

/**
 * Wrap store.commit / store.dispatch so unknown types are reported in prod too.
 * Vuex only console.errors these when NODE_ENV !== 'production'.
 * @param {import('vuex').Store<any>} store
 * @param {{ storeName?: string }} [options]
 * @returns {boolean} true when wrappers installed
 */
export function enableVuexUnknownCommitDispatchReport(store, options = {}) {
  if (!store || typeof store.commit !== 'function' || typeof store.dispatch !== 'function') {
    return false;
  }
  if (store.__vuexUnknownCommitDispatchWrapped) {
    return false;
  }

  const storeName = options.storeName || 'store';
  const originalCommit = store.commit.bind(store);
  const originalDispatch = store.dispatch.bind(store);

  store.commit = function wrappedCommit(type, payload, commitOptions) {
    const resolved = resolveVuexType(type);
    if (resolved && store._mutations && !store._mutations[resolved]) {
      reportUnknownMutation(storeName, resolved);
    }
    return originalCommit(type, payload, commitOptions);
  };

  store.dispatch = function wrappedDispatch(type, payload) {
    const resolved = resolveVuexType(type);
    if (resolved && store._actions && !store._actions[resolved]) {
      reportUnknownAction(storeName, resolved);
    }
    return originalDispatch(type, payload);
  };

  store.__vuexUnknownCommitDispatchWrapped = true;
  return true;
}

/**
 * Install mutation soft-report (sampled, prod) + unknown getter/action/mutation reporters.
 * @param {import('vuex').Store<any>} store
 * @param {{ storeName?: string }} [options]
 */
export function enableVuexSoftReports(store, options = {}) {
  enableVuexSoftIllegalMutationReport(store, options);
  enableVuexUnknownGetterReport(store, options);
  enableVuexUnknownCommitDispatchReport(store, options);
}
